import { PalmettoButton, PalmettoIcon, PalmettoSelect, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useMemo, useState } from 'react';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';

import { CountyRow } from './CountyRow';
import {
  EMPTY_COUNTY,
  getStateOptionsForRow,
  SystemLossThresholdsFormValues,
  validateMinPercent,
  validateSnowPercent,
  validateStateCode,
} from './formModel';

interface StateRowProps {
  index: number;
  control: Control<SystemLossThresholdsFormValues>;
  onRemove: (index: number) => void;
  hasCountyErrors: boolean;
  disabled?: boolean;
}

export const StateRow = ({ index, control, onRemove, hasCountyErrors, disabled }: StateRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: `entries.${index}.counties`,
  });
  const entries = useWatch({ control, name: 'entries' });

  const stateCode = entries[index]?.stateCode ?? '';
  const showCounties = isExpanded || hasCountyErrors;
  const stateLabel = stateCode || `row ${index + 1}`;

  const stateOptions = useMemo(() => getStateOptionsForRow(entries, index), [entries, index]);

  const stateCodeRules = useMemo(
    () => ({
      validate: (value: string, formValues: SystemLossThresholdsFormValues) =>
        validateStateCode(value, formValues.entries, index),
    }),
    [index],
  );

  const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

  const handleAddCounty = useCallback(() => {
    append(EMPTY_COUNTY);
    setIsExpanded(true);
  }, [append]);

  return (
    <div
      data-testid={`state-row-${index}`}
      className="flex flex-col gap-8 rounded-6 bg-canvas-highlight-default p-12"
    >
      <div className="flex flex-wrap items-start gap-8">
        <div className="w-[240px]">
          <Controller
            name={`entries.${index}.stateCode`}
            control={control}
            rules={stateCodeRules}
            render={({ field, fieldState }) => (
              <PalmettoSelect
                id={`state-code-${index}`}
                label="State"
                placeholder="Select state"
                items={stateOptions.map((option) => ({ ...option, selected: option.value === field.value }))}
                onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? '')}
                disabled={disabled}
                validationMessage={
                  fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                }
              />
            )}
          />
        </div>
        <div className="w-[160px]">
          <Controller
            name={`entries.${index}.snowLossPercent`}
            control={control}
            rules={{ validate: validateSnowPercent }}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id={`state-snow-${index}`}
                label="Snow loss"
                suffix="%"
                inputMode="decimal"
                helperText="Blank if none"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                validationMessage={
                  fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                }
              />
            )}
          />
        </div>
        <div className="w-[180px]">
          <Controller
            name={`entries.${index}.minSystemLossPercent`}
            control={control}
            rules={{ validate: validateMinPercent }}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id={`state-min-${index}`}
                label="Min system loss"
                suffix="%"
                inputMode="decimal"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                validationMessage={
                  fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                }
              />
            )}
          />
        </div>
        <div className="flex items-center gap-4 pt-8">
          <PalmettoButton
            type="button"
            emphasis={fields.length > 0 ? 'primary' : 'secondary'}
            layout="hug"
            size="small"
            onClick={handleToggle}
            disabled={disabled}
            aria-expanded={showCounties}
          >
            {`Counties (${fields.length})`}
          </PalmettoButton>
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="cursor-pointer border-none bg-transparent p-8 leading-none text-text-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Remove state ${stateLabel}`}
          >
            <PalmettoIcon name="trash" size="xs" />
          </button>
        </div>
      </div>

      <div hidden={!showCounties} className="flex flex-col gap-8 border-l border-border-default pl-16">
        {fields.length === 0 && (
          <p className="text-sm text-text-secondary">No county overrides. The state values apply to every county.</p>
        )}
        {fields.map((field, countyIndex) => (
          <CountyRow
            key={field.id}
            stateIndex={index}
            countyIndex={countyIndex}
            control={control}
            onRemove={remove}
            disabled={disabled}
          />
        ))}
        <div>
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            size="small"
            onClick={handleAddCounty}
            disabled={disabled}
          >
            {`Add county override for ${stateLabel}`}
          </PalmettoButton>
        </div>
      </div>
    </div>
  );
};
